import React, { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { userPrivileges } from '@apollo-internal/cache';
import { EventSettingsProvider } from '../contexts/EventSettingsContext';
import { APPRAISER_EVENT_TYPE } from '../../../constants/AppraiserEventType';
import { APPRAISER_EVENT_STAGE } from '../../../constants/AppraiserEventStage';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import AdminEditEvent from '../../CalendarEvents/EditEventPage/AdminEditEvent/AdminEditEvent';
import AppraiserEditEvent from '../../CalendarEvents/EditEventPage/AppraiserEditEvent/AppraiserEditEvent';
import { PAGE_MODE } from '../constants';
import { EVENT_FLAGS } from '@features/calendar/constants/EventFlags';

/**
 * @typedef {object} EventSettingsDataProviderProps
 * @property {React.ReactNode} children
 * @property {number} pageMode
 * @property {number} [eventStage]
 * @property {*} [eventType]
 * @property {boolean} [isAutoCreated]
 * @property {Array<*>} [eventFlags]
 */

/**
 * @param {EventSettingsDataProviderProps} props
 */
const EventSettingsDataProvider = props => {
  const { children, pageMode, eventStage, eventType, isAutoCreated, eventFlags } = props;
  const privileges = useReactiveVar(userPrivileges);
  const settings = EDIT_EVENT_PAGE_SETTINGS.find(
    x => x.pageMode === pageMode && (!x.eventType || x.eventType === eventType)
  ).settings;

  //Only precompute settings that are functions needing arguments
  const computedSettings = useMemo(
    () => settings(eventStage, eventType, privileges, isAutoCreated, eventFlags),
    [settings, eventStage, eventType, privileges, isAutoCreated, eventFlags]
  );

  return <EventSettingsProvider value={computedSettings}>{children}</EventSettingsProvider>;
};

const saveDescription =
  'Modify each editable field as needed. When done, click the “Save” button to update the event. ';
const cancelDescription =
  'Otherwise, click the “Cancel” button to return to the calendar and discard changes. ';
const changeDateRequestDescription =
  'Event Start Date cannot be moved more than 2 weeks. ' +
  'Click the Request Date Change button to seek approval for a start date outside the 2 week limit.';
const deleteDescription = 'The event can also be removed by clicking the “Delete” button.';
const bidForEventDescription =
  'View the event details below. Click on the Bid button to express your interest in participating on this event. NOTE: Event dates are subject to change.';
const readyToDeliverDescription =
  'This event is ready to deliver. Use the comment section to interact with the relevant stakeholders. ' +
  'No additional edits can be made at this time. If there is a change request, contact ISACA directly. ' +
  'Key Contacts can be found in the Resources page.';

const eventEditableAtlStages = [
  APPRAISER_EVENT_STAGE.ATL_ASSIGNED,
  APPRAISER_EVENT_STAGE.ATL_ASSIGNED_RETURNED
];

const atlSubmittableStages = [
  ...eventEditableAtlStages,
  APPRAISER_EVENT_STAGE.ATL_ASSIGNED_SUBMITTED
];

const atlEditableStages = [
  ...eventEditableAtlStages,
  APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID
];

const extendedAtlEditableStages = [
  ...atlEditableStages,
  APPRAISER_EVENT_STAGE.TEAM_ASSIGNED,
  APPRAISER_EVENT_STAGE.READY_TO_DELIVER
];

const sponsorEditableStages = [APPRAISER_EVENT_STAGE.DRAFT, ...eventEditableAtlStages];

const atlDateChangeRequestStages = [
  ...atlSubmittableStages,
  APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
  APPRAISER_EVENT_STAGE.TEAM_ASSIGNED
];
const sponsorDateChangeRequestStages = [APPRAISER_EVENT_STAGE.DRAFT, ...atlDateChangeRequestStages];

const sowTableStages = [
  APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
  APPRAISER_EVENT_STAGE.TEAM_ASSIGNED,
  APPRAISER_EVENT_STAGE.READY_TO_DELIVER
];

const eventAssignmentStages = [
  APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
  APPRAISER_EVENT_STAGE.TEAM_ASSIGNED,
  APPRAISER_EVENT_STAGE.READY_TO_DELIVER
];

const isRegularEvent = eventType => eventType === APPRAISER_EVENT_TYPE.REGULAR;

const isSponsorEditEnabled = eventStage => sponsorEditableStages.includes(eventStage);

const isOrgDateChangeRequestEnabled = (eventStage, eventType) => {
  if (eventType === APPRAISER_EVENT_TYPE.DIRECT_CHANNEL) {
    return false;
  }

  return sponsorDateChangeRequestStages.includes(eventStage);
};

const canUserEditBaseFields = eventStage =>
  eventStage < APPRAISER_EVENT_STAGE.ATL_ASSIGNED_SUBMITTED;

const STAGE_TO_PAGE_DESCRIPTION_MAP = {
  [APPRAISER_EVENT_STAGE.READY_TO_DELIVER]: readyToDeliverDescription,
  DEFAULT_ATM: bidForEventDescription,
  DEFAULT_NON_ATM: saveDescription + cancelDescription + changeDateRequestDescription
};

const getBaseAtlSettings = (eventStage, isAutoCreated) => ({
  description:
    STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
  component: AppraiserEditEvent,
  isSubmitEnabled: atlSubmittableStages.includes(eventStage),
  canEditBaseFields: canUserEditBaseFields(eventStage),
  canEditTeamAndTravel: eventEditableAtlStages.includes(eventStage),
  canRequestDateChange: atlDateChangeRequestStages.includes(eventStage),
  canEditDeliveryMethod: !isAutoCreated && eventEditableAtlStages.includes(eventStage),
  showAppraisalDetailStatus: true,
  showAssignmentRank: true,
  canViewAtl: true
});

const EDIT_EVENT_PAGE_SETTINGS = [
  {
    pageMode: PAGE_MODE.VIP_ADMIN,
    settings: (eventStage, eventType, privileges) => ({
      description: saveDescription + cancelDescription + deleteDescription,
      component: AdminEditEvent,
      isEditEnabled: true,
      canEditBaseFields: true,
      canEditTeamAndTravel: true,
      canEditAtlObserver:
        isRegularEvent(eventType) &&
        privileges.includes(PrivilegeNames.Calendar_EditEvent_Observation_ATL),
      canDeleteEvent: true,
      canFinalize: true,
      canReadyEvent: true,
      canAssignATMs: true,
      canEditDeliveryMethod: true,
      sowSettings: {
        canEdit: sowTableStages.includes(eventStage)
      },
      showEventAssignment: eventAssignmentStages.includes(eventStage),
      showAppraisalDetailStatus: true,
      showAssignmentRank: true,
      showAtmObservationSection:
        eventStage >= APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID &&
        privileges.includes(PrivilegeNames.Calendar_EditEvent_ATMObservationSection),
      showAtlRankingTable: isRegularEvent(eventType)
    })
  },

  {
    pageMode: PAGE_MODE.ISACA_APPRAISER_ATL,
    eventType: APPRAISER_EVENT_TYPE.REGULAR,
    settings: (eventStage, _, privileges, isAutoCreated, eventFlags) => {
      const hasFirstAppraisalForUnits = eventFlags?.includes(EVENT_FLAGS.FIRST_APPRAISAL_FOR_UNITS);
      const canEditAtmAssignment = privileges.includes(
        PrivilegeNames.Calendar_EditEvent_ATMAssignment
      );

      if (!canEditAtmAssignment || hasFirstAppraisalForUnits) {
        return {
          ...getBaseAtlSettings(eventStage, isAutoCreated),
          showEventAssignment: eventStage === APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
          isEditEnabled: atlEditableStages.includes(eventStage)
        };
      }

      return {
        ...getBaseAtlSettings(eventStage, isAutoCreated),
        showEventAssignment: eventAssignmentStages.includes(eventStage),
        isEditEnabled: extendedAtlEditableStages.includes(eventStage),
        canAssignATMs: true
      };
    }
  },
  {
    pageMode: PAGE_MODE.ISACA_APPRAISER_ATL,
    eventType: APPRAISER_EVENT_TYPE.DIRECT_CHANNEL,
    settings: eventStage => ({
      description: STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
      component: AppraiserEditEvent,
      isEditEnabled: true,
      isSubmitEnabled: atlSubmittableStages.includes(eventStage),
      isOrgUnitSelectorEnabled: true,
      canEditBaseFields: true,
      canEditTeamAndTravel: true,
      canRequestDateChange: atlDateChangeRequestStages.includes(eventStage),
      canAssignATMs: true,
      canReadyEvent: true,
      sowSettings: {
        canEdit: sowTableStages.includes(eventStage),
        editOnlyQualifiedAtms: true
      },
      canFinalize: true,
      canEditDeliveryMethod: true,
      showEventAssignment: eventAssignmentStages.includes(eventStage),
      showAppraisalDetailStatus: true,
      showIntendedDates: true
    })
  },

  {
    pageMode: PAGE_MODE.ATL_OBSERVER,
    settings: (eventStage, _, _1, isAutoCreated) => ({
      ...getBaseAtlSettings(eventStage, isAutoCreated),
      showEventAssignment: eventStage === APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
      isEditEnabled: atlEditableStages.includes(eventStage),
      canViewAtl: true,
      canViewAtlObserver: true
    })
  },
  {
    pageMode: PAGE_MODE.ASSIGNED_ATL_OBSERVER,
    settings: eventStage => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_ATM,
      component: AppraiserEditEvent,
      canViewAtlObserver: true,
      canViewAtl: true
    })
  },
  {
    pageMode: PAGE_MODE.ATM,
    settings: eventStage => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_ATM,
      component: AppraiserEditEvent,
      canBidOnEvent: true,
      canViewAtl: true
    })
  },
  {
    pageMode: PAGE_MODE.ORG,
    settings: (eventStage, eventType) => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
      component: AppraiserEditEvent,
      isEditEnabled: isSponsorEditEnabled(eventStage),
      canEditBaseFields: canUserEditBaseFields(eventStage),
      canRequestDateChange: isOrgDateChangeRequestEnabled(eventStage, eventType)
    })
  },
  {
    pageMode: PAGE_MODE.ORG_UNIT,
    settings: (eventStage, eventType) => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
      component: AppraiserEditEvent,
      isEditEnabled: isSponsorEditEnabled(eventStage),
      canEditBaseFields: canUserEditBaseFields(eventStage),
      canRequestDateChange: isOrgDateChangeRequestEnabled(eventStage, eventType)
    })
  },
  {
    pageMode: PAGE_MODE.ORG_ATM,
    settings: (eventStage, eventType) => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
      component: AppraiserEditEvent,
      isEditEnabled: isSponsorEditEnabled(eventStage),
      canEditBaseFields: canUserEditBaseFields(eventStage),
      canRequestDateChange: isOrgDateChangeRequestEnabled(eventStage, eventType)
    })
  },
  {
    pageMode: PAGE_MODE.ORG_UNIT_ATM,
    settings: (eventStage, eventType) => ({
      description:
        STAGE_TO_PAGE_DESCRIPTION_MAP[eventStage] || STAGE_TO_PAGE_DESCRIPTION_MAP.DEFAULT_NON_ATM,
      component: AppraiserEditEvent,
      isEditEnabled: isSponsorEditEnabled(eventStage),
      canEditBaseFields: canUserEditBaseFields(eventStage),
      canRequestDateChange: isOrgDateChangeRequestEnabled(eventStage, eventType)
    })
  },
  {
    pageMode: PAGE_MODE.TBD,
    settings: () => ({
      description: '',
      component: AppraiserEditEvent
    })
  }
];

export default EventSettingsDataProvider;
